import {
    byteArrayToBase64,
    encodeBase64Attachment,
} from '@qdabra/infopath-attachments';
import { equals } from 'ramda';
import { PDF_MIME_TYPE } from '../../../utility/mime.helpers.js';
import { xmlToString } from '../../../xmlUtility';
import {
    paramDsName,
    paramFileName,
    paramFooterAlignment,
    paramFooterFontFamily,
    paramFooterFontSize,
    paramFooterText,
    paramFormat,
    paramHeaderAlignment,
    paramHeaderFontFamily,
    paramHeaderFontSize,
    paramHeaderText,
    paramIncludeButtons,
    paramOpen,
    paramOutput,
    paramTemplateName,
    paramViews,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeOption,
} from '../constants/parameterTypes';
import { getViewText } from '../helpers/getViewText.helper.js';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';

const URL = window.URL || window.webkitURL;

const FORMAT_TYPE_PDF = 'pdf';
const FORMAT_TYPE_TEXT = 'text';
const OUTPUT_TYPE_BASE64 = 'base64';
const OUTPUT_TYPE_BASE64RAW = 'base64raw';
const OUTPUT_TYPE_TEXT = 'text';

const fontSize = 8;
const fontFamily = 'Arial';
const alignment = 'Left';

const openNone = 'none';
const openBrowser = 'browser';
const openDownload = 'download';
const openDefault = openBrowser;

const requiredParameters = [
    {
        name: paramFormat,
        description: `Format type to render form. ${FORMAT_TYPE_PDF} and ${FORMAT_TYPE_TEXT} are currently supported.`,
        type: typeOption,
        options: [
            {
                name: 'PDF',
                value: FORMAT_TYPE_PDF,
            },
            {
                name: 'text',
                value: FORMAT_TYPE_TEXT,
            },
        ],
        value: FORMAT_TYPE_PDF,
    },
];

const optionalParameters = [
    {
        name: paramViews,
        description: 'List of views to render, separated by &',
    },
    {
        name: paramOutput,
        description: `Output encoding. PDF default: ${OUTPUT_TYPE_BASE64}, allowed values for PDF: ${OUTPUT_TYPE_BASE64}, ${OUTPUT_TYPE_BASE64RAW}. Text default: ${OUTPUT_TYPE_TEXT}, allowed values for Text: ${OUTPUT_TYPE_TEXT})`,
        type: typeOption,
        options: [
            {
                name: 'base64',
                value: OUTPUT_TYPE_BASE64,
            },
            {
                name: 'base64raw',
                value: OUTPUT_TYPE_BASE64RAW,
            },
            {
                name: 'text',
                value: OUTPUT_TYPE_TEXT,
            },
        ],
    },
    {
        name: paramFileName,
        description: `Filename for the output file (only applies when /${paramOutput} is ${OUTPUT_TYPE_BASE64})`,
    },
    {
        name: paramTemplateName,
        description:
            'Name of the template to use for rendering data (defaults to the currently open template)',
    },
    {
        name: paramDsName,
        description: `Name of the data source to use as the "main" data source for rendering. Defaults to the form's actual main data source. Intended for use in conjunction with /${paramTemplateName}`,
        type: typeDataSource,
    },
    {
        name: paramHeaderFontFamily,
        description: `Font name to use for header (defaults to ${fontFamily})`,
    },
    {
        name: paramHeaderFontSize,
        description: `Font size to use for header in points (defaults to ${fontSize})`,
    },
    {
        name: paramHeaderText,
        description:
            'Text to use for header. Placeholders can be used for page numbers - {CrtPage} for current page, or {PageCount} for total pages.',
    },
    {
        name: paramHeaderAlignment,
        description: `Horizontal Alignment for header (valid values are left, center, right or justify - defaults to ${alignment})`,
    },
    {
        name: paramFooterFontFamily,
        description: `Font name to use for footer (defaults to ${fontFamily})`,
    },
    {
        name: paramFooterFontSize,
        description: `Font size to use for header in points (defaults to ${fontSize})`,
    },
    {
        name: paramFooterText,
        description:
            'Text to use for footer. Placeholders can be used for page numbers - {CrtPage} for current page, or {PageCount} for total pages.',
    },
    {
        name: paramFooterAlignment,
        description: `Horizontal Alignment for footer (valid values are left, center, right or justify - defaults to ${alignment})`,
    },
    {
        name: paramOpen,
        description: `Attempt to open the rendered file after creating it. Possible values are: ${openNone} (do nothing), ${openBrowser} (open within the browser), ${openDownload} (download the file). Default is: ${openDefault}. Note that actual behavior of /${paramOpen}=${openBrowser} and /${paramOpen}=${openDownload} can vary depending on browser and environment settings.`,
        type: typeOption,
        options: [
            {
                name: 'None',
                value: openNone,
            },
            {
                name: 'Browser',
                value: openBrowser,
            },
            {
                name: 'Download',
                value: openDownload,
            },
        ],
    },
    {
        name: paramIncludeButtons,
        description:
            '(boolean, false by default) Include button text when rendering form as text.',
        type: typeBoolean,
    },
];

const description =
    'Renders form data using a view from the current template or another template. Currently supports rendering as PDF or Text.';

let downloadUrl;

function viewNameIsValid(templateName, viewName, template) {
    // if templateName is specified, only verify that a viewName is specified since we
    // don't have immediate access to the template
    if (templateName) {
        return !!viewName;
    }

    return !!template.getViewFileName(viewName);
}

function validateParameters(
    format,
    output,
    viewName,
    templateName,
    open,
    template,
) {
    if (format !== FORMAT_TYPE_PDF && format !== FORMAT_TYPE_TEXT) {
        throw new Error(`Unsupported /${paramFormat} value: ${format}.`);
    }

    if (
        ![OUTPUT_TYPE_BASE64, OUTPUT_TYPE_BASE64RAW, OUTPUT_TYPE_TEXT].some(
            equals(output),
        )
    ) {
        throw new Error(`Unsupported /${paramOutput} value: ${output}.`);
    }

    if (
        (format === FORMAT_TYPE_PDF && output === OUTPUT_TYPE_TEXT) ||
        (format === FORMAT_TYPE_TEXT && output !== OUTPUT_TYPE_TEXT)
    ) {
        throw new Error(
            `Unsupported /${paramFormat} value: ${format} with /${paramOutput} value: ${output}.`,
        );
    }
    if (format === FORMAT_TYPE_TEXT && templateName) {
        throw new Error(
            `Cannot use /${paramTemplateName} with /${paramFormat}=${format}`,
        );
    }

    if (!viewNameIsValid(templateName, viewName, template)) {
        throw new Error(`Invalid view name: '${viewName}'`);
    }

    if (![openNone, openBrowser, openDownload].some(equals(open))) {
        throw new Error(`Invalid /${paramOpen} value: ${open}`);
    }
}

function getViewName(viewNames, templateName, viewManager) {
    if (!viewNames) {
        if (templateName) {
            throw new Error(
                `/${paramViews} must be specified when using /${paramTemplateName}`,
            );
        }

        return viewManager.getCurrentView();
    }

    var viewNameArray = viewNames.split('&');

    if (viewNameArray.length > 1) {
        throw new Error('Multiple views are not currently supported.');
    }

    return viewNameArray[0];
}

// ([Number], String) -> void
function downloadPdf(byteArray, fileName, openType) {
    try {
        // TODO: Use initiateFileDownload()/openFile() instead?

        if (downloadUrl) {
            // cleanup: remove element and revoke object URL
            URL.revokeObjectURL(downloadUrl);
        }

        var downloadLink = document.createElement('a');
        downloadLink.target = '_blank';
        downloadLink.download = fileName;

        // convert downloaded data to a Blob
        var blob = new Blob([new Uint8Array(byteArray)], {
            type: PDF_MIME_TYPE,
        });

        // create an object URL from the Blob
        downloadUrl = URL.createObjectURL(blob);

        if (openType === openBrowser) {
            downloadLink.onclick = function (e) {
                try {
                    e.preventDefault();
                    window.open(downloadUrl, '');
                } catch (error) {
                    console.error(
                        'Encountered error trying to open rendered file.',
                        e,
                    );
                }
            };
        }

        // set object URL as the anchor's href
        downloadLink.href = downloadUrl;

        // append the anchor to document body
        document.body.appendChild(downloadLink);

        // fire a click event on the anchor
        downloadLink.click();

        document.body.removeChild(downloadLink);
    } catch (e) {
        console.error(e);
    }
}

function getRenderResult(byteArray, output, fileName) {
    switch (output) {
        case OUTPUT_TYPE_BASE64:
            return encodeBase64Attachment(byteArray, fileName);

        case OUTPUT_TYPE_BASE64RAW:
            return byteArrayToBase64(new Uint8Array(byteArray));

        default:
            throw new Error(`Unknown /${paramOutput} type: ${output}`);
    }
}

async function renderTextAsync(domNode, viewName, transforms, includeButtons) {
    const data = await transforms.transformWithPreProcessedView(
        viewName,
        domNode,
    );
    const viewText = getViewText(data, includeButtons);
    return viewText;
}

function processGeneratePdfResult(data) {
    if (!(data && data.success)) {
        throw new Error(
            data.error ||
                'An unknown error occurred trying to generate the PDF.',
        );
    }

    return data.contents;
}

function renderPdfAsync(
    xmlString,
    viewNames,
    templateName,
    header,
    footer,
    api,
) {
    return api
        .generatePdfAsync(xmlString, viewNames, templateName, header, footer)
        .then(processGeneratePdfResult);
}

async function getFormTextAsync(viewName, dom, transforms, includeButtons) {
    return await renderTextAsync(
        dom.getNode(),
        viewName,
        transforms,
        includeButtons,
    );
}

async function performPdfRender(
    viewName,
    templateName,
    dom,
    header,
    footer,
    api,
    output,
    fileName,
    open,
) {
    const xmlString = xmlToString(dom.getNode());
    const viewNames = [viewName];

    const fileBytes = await renderPdfAsync(
        xmlString,
        viewNames,
        templateName,
        header,
        footer,
        api,
    );

    if (open !== openNone) {
        downloadPdf(fileBytes, fileName, open);
    }

    return getRenderResult(fileBytes, output, fileName);
}

async function performRender(
    viewName,
    format,
    templateName,
    header,
    footer,
    api,
    dataSources,
    transforms,
    includeButtons,
    output,
    fileName,
    open,
    dsName,
) {
    // TODO: getDataSource should return the main source if dsname is unspecified,
    //       so the conditional expression should be unnecessary
    const dom = getDataSource(dataSources, dsName ? dsName : '');

    if (format === FORMAT_TYPE_TEXT) {
        return await getFormTextAsync(
            viewName,
            dom,
            transforms,
            includeButtons,
        );
    }

    if (format === FORMAT_TYPE_PDF) {
        return await performPdfRender(
            viewName,
            templateName,
            dom,
            header,
            footer,
            api,
            output,
            fileName,
            open,
        );
    }

    throw new Error(`Unsupported /${paramFormat} value: ${format}`);
}

async function executeAsync({
    api,
    dataSources,
    parameters,
    viewManager,
    template,
    transforms,
}) {
    const viewNames = getParamValue(parameters, paramViews);
    const format = getParamValue(parameters, paramFormat);
    const output = getParamValue(
        parameters,
        paramOutput,
        format === 'pdf' ? 'base64' : 'text',
    );
    const fileName = getParamValue(parameters, paramFileName, 'rendered.pdf');

    const templateName = getParamValue(parameters, paramTemplateName);
    const dsName = getParamValue(parameters, paramDsName);
    const open = getParamValue(
        parameters,
        paramOpen,
        openDefault,
    ).toLowerCase();

    const viewName = getViewName(viewNames, templateName, viewManager);

    const header = {
        fontFamily: getParamValue(
            parameters,
            paramHeaderFontFamily,
            fontFamily,
        ),
        fontSize: getParamValue(parameters, paramHeaderFontSize, fontSize),
        text: getParamValue(parameters, paramHeaderText),
        alignment: getParamValue(parameters, paramHeaderAlignment, alignment),
    };

    const footer = {
        fontFamily: getParamValue(
            parameters,
            paramFooterFontFamily,
            fontFamily,
        ),
        fontSize: getParamValue(parameters, paramFooterFontSize, fontSize),
        text: getParamValue(parameters, paramFooterText),
        alignment: getParamValue(parameters, paramFooterAlignment, alignment),
    };

    const includeButtons = getBoolParamValue(
        parameters,
        paramIncludeButtons,
        false,
    );

    validateParameters(format, output, viewName, templateName, open, template);

    return performRender(
        viewName,
        format,
        templateName,
        header,
        footer,
        api,
        dataSources,
        transforms,
        includeButtons,
        output,
        fileName,
        open,
        dsName,
    );
}

const RenderForm = {
    executeAsync,
    description,
    optionalParameters,
    requiredParameters,
};

export default RenderForm;
